import { useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';
import {
  QuoteFilterOptions,
  quoteRequestParams,
  QuoteTags,
  ServiceType,
  ServiceTypes,
  ToServiceTagDefaultLabel,
  ToServiceTypeDefaultLabel
} from '~/lib/quotes';
import { FilterInner } from './quote-filter-option';
import QuoteFilterSection from './quote-filter-section';
import CollectionRequired from './quote-filter-collection-required';
import DropOffRequired from './quote-filter-drop-off';
import { P2GGrid } from '@parcel2go/core-ui';
import { BasketQuote } from '~/lib/types/basket-types';
import { onlyUniqueNonBlank } from '~/lib/utils/array';
import { useMemo } from 'react';
import { useTenant } from '@parcel2go/npm-p2g-app';
import { useSearchParams } from 'react-router-dom';
import { PublicSettings } from '~/lib/ui-serverless-settings';

export default function QuoteFilterServiceType({
  filter,
  onChange,
  title,
  dataTestId,
  questions,
  quotes
}: {
  title: string;
  dataTestId?: string;
  filter: QuoteFilterOptions;
  onChange: (filter: QuoteFilterOptions) => void;
  questions: boolean;
  quotes: BasketQuote[];
}) {
  const { t } = useTranslation([TranslationNamespaces.QUOTE]);
  const { settings } = useTenant();
  const [searchParams] = useSearchParams();
  const { origin, destination } = quoteRequestParams(searchParams, settings);
  const domesticQuote = origin === destination;
  const sameDayQuoteEnabled = (settings[PublicSettings.QUOTES.SAMEDAYENABLED] ?? 'false') === 'true';

  const listServiceTypes = useMemo(() => {
    return sameDayQuoteEnabled && domesticQuote ? ServiceTypes : ServiceTypes.filter((x) => x !== 'SameDay');
  }, [sameDayQuoteEnabled, domesticQuote]);

  const tags = useMemo(
    () =>
      quotes
        ?.flatMap((x) => x.tags)
        .filter(onlyUniqueNonBlank)
        .filter((x) => x === QuoteTags.DDP),
    [quotes]
  );

  if (questions) {
    return (
      <QuoteFilterSection title={title} dataTestId={dataTestId}>
        <P2GGrid container direction="column" spacing={2}>
          <P2GGrid item>
            <CollectionRequired
              filter={filter}
              onChange={onChange}
              label={t('FILTER_COLLECTION_REQUIRED_LABEL', { defaultValue: 'I need my parcel collecting' })}
            />
          </P2GGrid>
          <P2GGrid item>
            <DropOffRequired
              filter={filter}
              onChange={onChange}
              label={t('FILTER_DROPOFF_REQUIRED_LABEL', { defaultValue: 'I want to drop this off myself' })}
            />
          </P2GGrid>
        </P2GGrid>
      </QuoteFilterSection>
    );
  }

  return (
    <QuoteFilterSection title={title} dataTestId={dataTestId}>
      <P2GGrid container spacing={1} direction="column">
        <P2GGrid item>
          <FilterInner
            options={listServiceTypes.map((s) => ({
              type: s,
              label: t(`FILTER_SERVICE_TYPE_OPTION_${s}`.toUpperCase(), { defaultValue: ToServiceTypeDefaultLabel(s) })
            }))}
            selected={[filter.serviceType]}
            onChange={(v) =>
              onChange({
                ...filter,
                serviceType: filter.serviceType === (v as ServiceType) ? undefined : (v as ServiceType)
              })
            }
          />
        </P2GGrid>
        {tags.length > 0 && (
          <P2GGrid item>
            <FilterInner
              options={tags.map((s) => ({
                key: s,
                type: s,
                label: t(`FILTER_SERVICE_TAG_OPTION_${s}`.toUpperCase(), {
                  defaultValue: ToServiceTagDefaultLabel(s)
                })
              }))}
              selected={filter.tags}
              onChange={(v) => {
                onChange({
                  ...filter,
                  tags: filter.tags?.includes(v) ? filter.tags.filter((x) => x !== v) : [...(filter.tags || []), v]
                });
              }}
            />
          </P2GGrid>
        )}
      </P2GGrid>
    </QuoteFilterSection>
  );
}
